import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="flex gap-3 group cursor-pointer"
      >
        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
            {article.title}
          </h4>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-xs text-foreground-500">{article.date}</span>
            <span className="text-xs text-foreground-400">{article.views.toLocaleString()} 阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="block group cursor-pointer"
      >
        <div className="relative rounded-xl overflow-hidden aspect-[16/10]">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
            <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50 mb-2">
              {article.category}
            </span>
            <h3 className="text-lg md:text-xl font-semibold text-white line-clamp-2 leading-snug">
              {article.title}
            </h3>
            <p className="text-sm text-white/70 mt-1 line-clamp-2 hidden sm:block">{article.excerpt}</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-white/60">
              <span>{article.date}</span>
              <span>{article.views.toLocaleString()} 阅读</span>
            </div>
          </div>
          {article.isNew && (
            <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-[10px] font-bold bg-accent-500 text-background-50">
              NEW
            </span>
          )}
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="block group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="relative overflow-hidden aspect-[16/10]">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {article.isHot && (
          <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary-500 text-background-50">
            热门
          </span>
        )}
        {article.isNew && !article.isHot && (
          <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-[10px] font-bold bg-accent-500 text-background-50">
            NEW
          </span>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[11px] font-medium text-primary-600 bg-primary-100 px-2 py-0.5 rounded-full">
            {article.category}
          </span>
        </div>
        <h3 className="text-base font-semibold text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 mt-2 line-clamp-2 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
          <div className="flex items-center gap-1 w-4 h-4">
            <i className="ri-calendar-line text-xs"></i>
          </div>
          <span>{article.date}</span>
          <div className="flex items-center gap-1 w-4 h-4 ml-2">
            <i className="ri-eye-line text-xs"></i>
          </div>
          <span>{article.views.toLocaleString()}</span>
        </div>
      </div>
    </Link>
  );
}